import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowLeft, CheckCircle2, Lock, Sparkles } from 'lucide-react';

const PASSWORD_RESET_SUCCESS_KEY = 'supabase.password-reset-success';
const SIGNUP_PENDING_KEY = 'supabase.signup-pending';

const PasswordResetUpdate = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const passwordMismatch = useMemo(
    () => password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword,
    [password, confirmPassword]
  );

  const canSubmit =
    !passwordMismatch && password.length >= 6 && confirmPassword.length >= 6 && !submitting;

  useEffect(() => {
    let isMounted = true;
    const verifySession = async () => {
      if (!supabase) {
        if (!isMounted) return;
        setMessage('Supabase クライアントが初期化されていません。環境変数をご確認ください。');
        setMessageType('error');
        return;
      }
      const { data, error } = await supabase.auth.getSession();
      if (!isMounted) return;

      if (error || !data.session) {
        setMessage('リンクの有効期限が切れているか、すでに使用されています。再度メールを送信してください。');
        setMessageType('error');
        return;
      }

      if (typeof window !== 'undefined' && window.sessionStorage.getItem(SIGNUP_PENDING_KEY)) {
        setMessage('初回登録のため、新しいパスワードを設定してください。');
        setMessageType('info');
      }
    };

    void verifySession();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const client = supabase;
    if (!client) {
      setMessage('Supabase クライアントが初期化されていません。環境変数をご確認ください。');
      setMessageType('error');
      return;
    }

    if (!canSubmit) return;

    setSubmitting(true);
    setMessage(null);
    setMessageType(null);

    try {
      const { error } = await client.auth.updateUser({ password });
      if (error) {
        setMessage(error.message);
        setMessageType('error');
        return;
      }

      setMessage('パスワードを更新しました。ログイン画面へ戻ります。');
      setMessageType('success');

      setTimeout(async () => {
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(PASSWORD_RESET_SUCCESS_KEY, 'true');
          window.sessionStorage.removeItem(SIGNUP_PENDING_KEY);
        }
        await client.auth.signOut();
        navigate('/login', { replace: true });
      }, 1500);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <header className="bg-gray-800 px-6 py-4 text-white shadow">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500 shadow-inner">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-lg font-semibold tracking-wide">勤怠管理システム</p>
              <p className="text-xs text-blue-100">新しいパスワードの設定</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <ArrowLeft className="h-4 w-4" />
            ログインへ戻る
          </button>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl ring-1 ring-gray-100">
          <h1 className="text-2xl font-semibold text-gray-900">新しいパスワードを設定</h1>
          <p className="mt-2 text-sm text-gray-600">
            セキュリティのため、6文字以上の新しいパスワードを入力してください。
          </p>

          {message && (
            <div
              className={[
                'mt-6 rounded-lg border px-4 py-3 text-sm',
                messageType === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : messageType === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
              ].join(' ')}
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <label className="block text-left">
              <span className="text-sm font-medium text-gray-700">新しいパスワード</span>
              <div className="relative mt-2">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={6}
                  disabled={submitting}
                  placeholder="6文字以上で設定してください"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-10 py-3 text-gray-900 shadow-sm transition focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              </div>
            </label>

            <label className="block text-left">
              <span className="text-sm font-medium text-gray-700">パスワード（確認）</span>
              <div className="relative mt-2">
                <CheckCircle2 className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  disabled={submitting}
                  placeholder="同じパスワードを入力してください"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-10 py-3 text-gray-900 shadow-sm transition focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              </div>
              {passwordMismatch && (
                <p className="mt-2 text-xs text-red-600">パスワードが一致していません。</p>
              )}
            </label>

            <button
              type="submit"
              disabled={!canSubmit}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              パスワードを更新
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default PasswordResetUpdate;
